import { isDefined, isNotDefined } from '@sgme/fp';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CommandsCellRenderer } from '@/App/Blotter/grids/accumulator/CommandsCellRenderer';
import { DateRenderer, StatusCellRenderer } from '@/App/Blotter/grids/commonCellRenderers.tsx';
import { basicTimeFormat, dateFormatterBuilder } from '@/App/Blotter/grids/commonFormatters.ts';
import {
  dateFilterDefinition,
  setFilterDefinition,
  textFilterDefinition,
} from '@/App/Blotter/grids/filterDefinitions.ts';
import type { BlotterAccumulatorEntry } from '@/store/blotter/types/BlotterAccumulatorEntry.ts';
import type { ISODateTime } from '@/types/date.ts';

export function useColumnDefinitions() {
  const intl = useIntl();
  return useMemo<ColDef<BlotterAccumulatorEntry>[]>(() => {
    const { formatMessage } = intl;

    const dateFormatter = dateFormatterBuilder<BlotterAccumulatorEntry>(intl);

    return [
      {
        field: 'id',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.id' }),
        width: 150,
        lockPosition: true,
        ...textFilterDefinition,
      },
      {
        colId: 'actions',
        valueGetter: ({ data }) => data?.id,
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.actions' }),
        cellRenderer: CommandsCellRenderer,
        width: 100,
        filter: false,
        sortable: false,
      },
      {
        field: 'account',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.account' }),
        width: 200,
        ...textFilterDefinition,
      },
      {
        field: 'currencyPair',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.currencyPair' }),
        width: 140,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currency pairs in the grid)
      },
      {
        colId: 'tradeDate',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.tradeDate' }),
        valueGetter: ({ data }) => data?.execTime,
        cellRenderer: DateRenderer,
        width: 180,
        ...dateFilterDefinition,
      },
      {
        field: 'portfolio',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.portfolio' }),
        width: 120,
        ...textFilterDefinition,
      },
      {
        field: 'fixingFrequency',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.fixingFrequency' }),
        width: 120,
        ...textFilterDefinition,
      },
      {
        field: 'settlementFrequency',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.settlementFrequency' }),
        width: 120,
        ...textFilterDefinition,
      },
      {
        field: 'koConventionType',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.koConventionType' }),
        width: 120,
        ...textFilterDefinition,
      },
      {
        field: 'koTime',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.koTime' }),
        valueFormatter: dateFormatter,
        width: 150,
        ...dateFilterDefinition,
      },
      {
        field: 'premium',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.premium' }),
        width: 110,
        ...textFilterDefinition,
      },
      {
        field: 'product',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.product' }),
        width: 100,
        ...setFilterDefinition,
      },
      {
        field: 'sales',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.sales' }),
        width: 250,
        ...textFilterDefinition,
      },
      {
        field: 'status',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.status' }),
        cellRenderer: StatusCellRenderer,
        width: 170,
        ...setFilterDefinition,
      },
      {
        field: 'execTime',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.execTime' }),
        valueGetter: ({ data }) => data?.execTime,
        valueFormatter: ({ value }: { value: ISODateTime }) => basicTimeFormat(value),
        width: 140,
        // no filter here : displayed times are not comparable because they (most probably) belong to different dates
      },
      {
        field: 'venue',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.venue' }),
        width: 120,
        ...setFilterDefinition, // unsure, maybe text filter here, depending on the potential size of the set
      },
      {
        field: 'way',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.way' }),
        width: 110,
        ...setFilterDefinition,
      },
      {
        field: 'expiry',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.expiry' }),
        valueFormatter: ({ value, node, ...rest }: ValueFormatterParams<BlotterAccumulatorEntry, ISODateTime>) =>
          // row groups emanating for multileg options has a meaningless expiry field
          (isDefined(node?.group) && node.group) || isNotDefined(value) ? '' : dateFormatter({ value, node, ...rest }),
        width: 120,
        ...dateFilterDefinition,
      },
      {
        field: 'premiumCurrency',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.premiumCurrency' }),
        width: 160,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
      },
      {
        field: 'premiumSide',
        headerName: formatMessage({ id: 'blotter.tab.Accumulator.column.premiumSide' }),
        width: 140,
        ...setFilterDefinition,
      },
    ];
  }, []);
}
